"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Sun, Users, Cloud } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function LoversCardPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-pink-500/10 border border-pink-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-pink-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-pink-500">/</span>
            <span className="text-sm">Rider-Waite</span>
            <span className="text-pink-500">/</span>
            <span className="text-sm">Majör Arkana</span>
            <span className="text-pink-500">/</span>
            <span className="text-sm">The Lovers</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Rider-Waite
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite/major-arcana"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Majör Arkana
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">The Lovers</span>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-pink-900/20 to-rose-900/20 rounded-xl p-8 mb-8 border border-pink-500/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/rider-waite-lovers.svg"
                  alt="The Lovers Tarot Card"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-700 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">VI</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">The Lovers</h1>
                  <p className="text-xl text-pink-400">Aşıklar / Birlik</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Aşk, birlik ve seçimlerin kartı. İki kalbin birleşmesi ve divine rehberlik altında
                yapılan önemli kararların temsilidir.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-pink-500/20 text-pink-300 rounded-full text-sm">Aşk</span>
                <span className="px-3 py-1 bg-pink-500/20 text-pink-300 rounded-full text-sm">Birlik</span>
                <span className="px-3 py-1 bg-pink-500/20 text-pink-300 rounded-full text-sm">Seçimler</span>
                <span className="px-3 py-1 bg-pink-500/20 text-pink-300 rounded-full text-sm">Uyum</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sun className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Gerçek Aşk</h3>
                  <p className="text-slate-400 text-sm">Derin bağlantı, ruh eşi, karşılıklı sevgi.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Uyumlu Birliktelik</h3>
                  <p className="text-slate-400 text-sm">Partnerlık, işbirliği ve karşılıklı destek.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Önemli Seçimler</h3>
                  <p className="text-slate-400 text-sm">Hayat değiştiren kararlar, değer temelli tercihler.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Çekicilik</h3>
                  <p className="text-slate-400 text-sm">Magnetik çekim, karizma ve cazibe.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Cloud className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">İlişki Sorunları</h3>
                  <p className="text-slate-400 text-sm">Uyumsuzluk, çatışma, iletişim problemleri.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Kötü Seçimler</h3>
                  <p className="text-slate-400 text-sm">Yanlış kararlar, temptasyon ve değer çelişkisi.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sadakatsizlik</h3>
                  <p className="text-slate-400 text-sm">İhanet, aldatma ve güven kaybı.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Kararsızlık</h3>
                  <p className="text-slate-400 text-sm">Seçim yapamama, ikilem ve belirsizlik.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Sembolik Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">👼</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Melek Raphael</h3>
              <p className="text-slate-400 text-sm">Divine rehberlik, ruhani koruma ve kutsal onay.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-pink-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <Users className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Adam ve Kadın</h3>
              <p className="text-slate-400 text-sm">Tamamlayıcı karşıtlıklar, birlik ve denge.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🌳</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Yaşam Ağacı</h3>
              <p className="text-slate-400 text-sm">Büyüme, yaşam gücü ve doğal döngüler.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🍎</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Bilgi Ağacı</h3>
              <p className="text-slate-400 text-sm">Seçim, ayartma ve bilinç gelişimi.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🔥</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Yanan Tutkular</h3>
              <p className="text-slate-400 text-sm">Aşk ateşi, tutku ve içsel enerji.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">⛰️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Kutsal Dağ</h3>
              <p className="text-slate-400 text-sm">Ruhani yükselme, divine bağlantı ve aşkınlık.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Yeni aşk ilişkisi</li>
                <li>• Evlilik ve nişan</li>
                <li>• Ruh eşi bulma</li>
                <li>• İlişkide derin bağ</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• İş ortaklığı kurma</li>
                <li>• Yaratıcı projeler</li>
                <li>• Müşteri ilişkileri</li>
                <li>• Önemli iş kararları</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Sun className="h-5 w-5 text-green-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Değerlerini keşfetme</li>
                <li>• Seçim yapma becerisi</li>
                <li>• İlişki becerilerini geliştirme</li>
                <li>• Karşıt güçleri dengeleme</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Cloud className="h-5 w-5 text-blue-400" />
                Ruhsal Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Divine rehberlik alma</li>
                <li>• Ruhsal birliktelik</li>
                <li>• Kozmik uyum</li>
                <li>• İç bütünlük</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-amber-100 leading-relaxed">
            Bu kart anlamları geleneksel tarot yorumlarına dayanmaktadır ve sadece eğlence ve kişisel farkındalık amaçlıdır.
            Yaşamsal kararlar için uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/rider-waite/major-arcana/hierophant"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            The Hierophant
          </Link>

          <Link
            href="/about/tarot/systems/rider-waite/major-arcana/chariot"
            className="inline-flex items-center px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white rounded-lg transition-colors"
          >
            The Chariot
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}